import { Slot } from '@radix-ui/react-slot';
import { type ReactElement, useEffect, useMemo, useRef } from 'react';
import {
  GridLayout,
  type GridLayoutOptions,
  ListLayout,
  type ListLayoutOptions,
  Rect,
  Size,
  Virtualizer
} from 'react-aria-components';
import { useDragEdgeScroll } from './useDragEdgeScroll';
import { boxesMeet } from 'v5/patterns/page-layout/marqueeGeometry';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import type { ItemsInRect, TileLayout } from './tileLayouts';

/** The drop place's box: the line's whole drawing, end circles included (the box clips the rest). */
const DROP_LINE_PX = 10;

interface FillingGridOptions extends GridLayoutOptions {
  tileMinWidth: number;
  tileHeight(tileWidth: number): number;
  gap: number;
  columns?: number;
}

/**
 * React Aria's grid with its columns stretched over the whole row, as a CSS grid's `1fr`.
 * `withPage`: see `PageListLayout`.
 */
class FillingGridLayout<T> extends GridLayout<T, FillingGridOptions> {
  private readonly withPage: boolean;
  private readonly computedHeights: boolean;

  constructor(withPage = false, computedHeights = false) {
    super();
    this.withPage = withPage;
    this.computedHeights = computedHeights;
  }

  // the tiles keep the heights the layout computes: a measured one, once it differs, relayouts the
  // list, and a tile that measures again on every relayout never settles
  override updateItemSize(
    ...args: Parameters<GridLayout<T, FillingGridOptions>['updateItemSize']>
  ): boolean {
    return this.computedHeights ? false : super.updateItemSize(...args);
  }

  override shouldInvalidate(next: Rect, previous: Rect): boolean {
    return this.withPage
      ? next.width !== previous.width
      : super.shouldInvalidate(next, previous);
  }

  override getDropTargetFromPoint(
    ...[x, y, isValidDropTarget]: Parameters<
      GridLayout<T, FillingGridOptions>['getDropTargetFromPoint']
    >
  ) {
    const shift = this.withPage ? this.virtualizer?.visibleRect : undefined;
    return super.getDropTargetFromPoint(
      x - (shift?.x ?? 0),
      y - (shift?.y ?? 0),
      isValidDropTarget
    );
  }

  override shouldInvalidateLayoutOptions(
    next: FillingGridOptions,
    previous: FillingGridOptions
  ): boolean {
    return (
      next.tileMinWidth !== previous.tileMinWidth ||
      next.gap !== previous.gap ||
      next.columns !== previous.columns ||
      next.dropIndicatorThickness !== previous.dropIndicatorThickness
    );
  }

  override update(
    context: Parameters<GridLayout<T, FillingGridOptions>['update']>[0]
  ): void {
    const options = context.layoutOptions;
    if (!options) return super.update(context);
    const { tileMinWidth, gap, columns = Infinity } = options;
    const width = this.virtualizer?.size.width ?? 0;
    const count = Math.max(
      1,
      Math.min(columns, Math.floor((width - gap) / (tileMinWidth + gap)))
    );
    // GridLayout floors the space it leaves between the tiles: a hair narrower, it stays `gap`
    const tileWidth = (width - (count + 1) * gap) / count - 0.001;
    // a tile laid out with no width (the list not sized yet) would keep its 0 height, as a
    // measured one keeps its own, and never be drawn: nothing until there is room
    if (tileWidth <= 0) return;
    const size = new Size(tileWidth, options.tileHeight(tileWidth));
    super.update({
      ...context,
      layoutOptions: {
        ...options,
        minItemSize: size,
        maxItemSize: size,
        minSpace: new Size(gap, gap),
        maxColumns: count,
        // a measured tile keeps the height it drew; a computed one is `tileHeight` at every width,
        // since React Stately keeps an estimated tile's old height on a width-only change
        preserveAspectRatio: this.computedHeights
      }
    });
  }
}

/**
 * React Aria's list in a page that scrolls it: its visible rectangle is where the page shows the
 * list (React Aria's window scrolling), so it moves and changes height as the page scrolls, which
 * lays nothing out anew (only a new width does); and a drop's point, read from the list's own box,
 * is already where the content is, not where its view starts.
 */
class PageListLayout<T> extends ListLayout<T> {
  override shouldInvalidate(next: Rect, previous: Rect): boolean {
    return next.width !== previous.width;
  }

  override getDropTargetFromPoint(
    ...[x, y, isValidDropTarget]: Parameters<
      ListLayout<T>['getDropTargetFromPoint']
    >
  ) {
    const shift = this.virtualizer?.visibleRect;
    return super.getDropTargetFromPoint(
      x - (shift?.x ?? 0),
      y - (shift?.y ?? 0),
      isValidDropTarget
    );
  }
}

/**
 * Only the tiles on screen are drawn: as many columns at least `tileMinWidth` wide as fit, at
 * most `columns`, stretched to fill the row (one column never wider than the list), every tile
 * `tileHeight(its width)` tall until drawn, then as tall as it draws, `gap` between them and at
 * the edges; the list fills its host's height and scrolls in it, with soft edges where it is
 * scrolled past (or the plain shadow, `edge="shadow"`). With `scrollsWithPage` it is as tall as
 * its tiles and the page around scrolls it, with the page's edges. A thousand tiles stay cheap.
 * `computedHeights`: the tiles are as tall as `tileHeight` says, never as tall as they measure.
 */
export function virtualGridLayout({
  tileMinWidth,
  tileHeight,
  gap,
  columns,
  edge = 'soft',
  scrollsWithPage = false,
  computedHeights = false
}: {
  tileMinWidth: number;
  /** The tile's height at a width (a fixed proportion, or one plus what sits under a picture). */
  tileHeight(tileWidth: number): number;
  gap: number;
  columns?: number;
  /** How an edge scrolled past shows it (`ScrollShadowWrapper`). */
  edge?: ScrollEdge;
  /** An element around scrolls the list (a page with a header above it), not the list itself. */
  scrollsWithPage?: boolean;
  /** See `virtualGridLayout`. */
  computedHeights?: boolean;
}): TileLayout {
  return {
    // with the page: at least down to the bottom of its host's column, so a drop under the tiles lands
    className: scrollsWithPage
      ? // a whole number of pixels wide: React Aria lays the tiles out over the list's
        // `clientWidth`, which rounds a fractional width up, and a row would stand out of the page
        // by its half pixel (a scroll bar across); `overflow-y-auto` is `useDragEdgeScroll`'s
        'flex-1 w-[round(down,100%,1px)] overflow-x-hidden overflow-y-auto'
      : 'with-styled-scrollbars with-styled-scrollbars__transparent overflow-x-hidden',
    fills: !scrollsWithPage,
    grows: scrollsWithPage,
    // between tiles side by side; a single column stacks, as a list does
    dropLine: columns === 1 ? 'horizontal' : 'vertical',
    wrap: (list, answersItemsInRect) => (
      <VirtualTiles
        list={list}
        answersItemsInRect={answersItemsInRect}
        kind="grid"
        edge={edge}
        scrollsWithPage={scrollsWithPage}
        computedHeights={computedHeights}
        options={{
          tileMinWidth,
          tileHeight,
          gap,
          columns,
          dropIndicatorThickness: DROP_LINE_PX
        }}
      />
    )
  };
}

/** Rows `rowHeight` high, only the ones on screen drawn; otherwise as `virtualGridLayout`. */
export function virtualListLayout({
  rowHeight,
  edge = 'soft',
  scrollsWithPage = false
}: {
  rowHeight: number;
  edge?: ScrollEdge;
  scrollsWithPage?: boolean;
}): TileLayout {
  return {
    // with the page: at least down to the bottom of its host's column, so a drop under the tiles lands
    className: scrollsWithPage
      ? // a whole number of pixels wide: React Aria lays the tiles out over the list's
        // `clientWidth`, which rounds a fractional width up, and a row would stand out of the page
        // by its half pixel (a scroll bar across); `overflow-y-auto` is `useDragEdgeScroll`'s
        'flex-1 w-[round(down,100%,1px)] overflow-x-hidden overflow-y-auto'
      : 'with-styled-scrollbars with-styled-scrollbars__transparent overflow-x-hidden',
    arrows: 'stack',
    fills: !scrollsWithPage,
    grows: scrollsWithPage,
    dropLine: 'horizontal',
    wrap: (list, answersItemsInRect) => (
      <VirtualTiles
        list={list}
        answersItemsInRect={answersItemsInRect}
        kind="list"
        edge={edge}
        scrollsWithPage={scrollsWithPage}
        options={{ rowHeight, dropIndicatorThickness: DROP_LINE_PX }}
      />
    )
  };
}

/**
 * The virtualizer around the list, one component for the grid and the list so a switch between
 * them keeps the list mounted (its focus, its scroller). It tells the collection where every item
 * stands, drawn or not, from the layout.
 */
type ScrollEdge = 'soft' | 'shadow';

function VirtualTiles({
  list,
  answersItemsInRect,
  edge,
  scrollsWithPage,
  computedHeights = false,
  ...layoutProps
}: {
  list: ReactElement;
  edge: ScrollEdge;
  scrollsWithPage: boolean;
  computedHeights?: boolean;
  answersItemsInRect: (itemsInRect: ItemsInRect | null) => void;
} & (
  | { kind: 'grid'; options: FillingGridOptions }
  | { kind: 'list'; options: ListLayoutOptions }
)) {
  const { kind } = layoutProps;
  // a new layout for the other kind, as the virtualizer makes one for another class
  const layout = useMemo(
    () =>
      kind === 'grid'
        ? {
            kind,
            instance: new FillingGridLayout(scrollsWithPage, computedHeights)
          }
        : {
            kind,
            instance: scrollsWithPage ? new PageListLayout() : new ListLayout()
          },
    [kind, scrollsWithPage, computedHeights]
  );
  const scrollerRef = useRef<HTMLDivElement>(null);
  useDragEdgeScroll(scrollerRef, scrollsWithPage);
  useEffect(() => {
    answersItemsInRect(rect => {
      const scroller = scrollerRef.current;
      if (!scroller) return [];
      // the layout's coordinates are the scroller's content's
      const box = scroller.getBoundingClientRect();
      const dx = scroller.scrollLeft - box.left - scroller.clientLeft;
      const dy = scroller.scrollTop - box.top - scroller.clientTop;
      const area = {
        left: rect.left + dx,
        top: rect.top + dy,
        right: rect.right + dx,
        bottom: rect.bottom + dy
      };
      // React Aria's rectangles never meet a flat one (a drag straight across): asked a pixel
      // wider, checked by touch as the drawn items are; it also adds what it keeps drawn anywhere
      // (the focused item)
      return layout.instance
        .getVisibleLayoutInfos(
          new Rect(
            area.left - 1,
            area.top - 1,
            area.right - area.left + 2,
            area.bottom - area.top + 2
          )
        )
        .filter(
          ({ type, rect: { x, y, maxX, maxY } }) =>
            type === 'item' &&
            boxesMeet(area, { left: x, top: y, right: maxX, bottom: maxY })
        )
        .map(info => String(info.key));
    });
    return () => answersItemsInRect(null);
  }, [layout, answersItemsInRect]);
  const scroller = scrollsWithPage ? (
    <Slot ref={scrollerRef}>{list}</Slot>
  ) : (
    <ScrollShadowWrapper
      ref={scrollerRef}
      edge={edge}
      asChild
      className="min-h-0 flex-1"
    >
      {list}
    </ScrollShadowWrapper>
  );
  // both from the same `kind`: the pairs below are the only ones
  if (layout.kind === 'grid' && layoutProps.kind === 'grid') {
    return (
      <Virtualizer layout={layout.instance} layoutOptions={layoutProps.options}>
        {scroller}
      </Virtualizer>
    );
  }
  if (layout.kind === 'list' && layoutProps.kind === 'list') {
    return (
      <Virtualizer layout={layout.instance} layoutOptions={layoutProps.options}>
        {scroller}
      </Virtualizer>
    );
  }
  return null;
}
